/**
 * DrillDownFilterBar — BUILD-14.
 *
 * Renders a breadcrumb + active filter chips + "Clear all" whenever
 * drill-down params are present in the URL.
 *
 * Usage:
 *   <DrillDownFilterBar
 *     backLabel="Manager Dashboard"
 *     backHref="/dashboards/manager"
 *     filters={{ owner: "Yvonne Lin", stage: "Proposal" }}
 *     onClear={() => setLocation('/pipeline')}
 *     onRemove={(key) => { ... remove single param ... }}
 *   />
 */

import { tokens } from "@/components/operator/tokens";

interface Props {
  /** Human-readable label for the "back" link */
  backLabel: string;
  /** Hash href for the "back" link, e.g. "/dashboards/manager" */
  backHref: string;
  /**
   * Applied filters to display as chips.
   * key = display label, value = display value.
   * Only entries with truthy values are rendered.
   */
  filters: Record<string, string | null | undefined>;
  /** Called when "Clear all" is clicked. Should navigate back or remove all params. */
  onClear: () => void;
  /**
   * Called when an individual chip's X is clicked.
   * key matches the key in `filters`.
   */
  onRemove?: (key: string) => void;
}

export default function DrillDownFilterBar({
  backLabel,
  backHref,
  filters,
  onClear,
  onRemove,
}: Props) {
  const activeFilters = Object.entries(filters).filter(([, v]) => v != null && v !== "");

  if (activeFilters.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        padding: "8px 0 16px",
        borderBottom: `1px solid ${tokens.color.border}`,
        marginBottom: 20,
      }}
    >
      {/* Back breadcrumb */}
      <a
        href={`#${backHref}`}
        style={{
          fontSize: 12,
          color: tokens.color.textMuted,
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.color = tokens.color.textPrimary;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.color = tokens.color.textMuted;
        }}
      >
        ← {backLabel}
      </a>

      <span style={{ fontSize: 11, color: tokens.color.textSubtle }}>·</span>

      <span style={{ fontSize: 12, color: tokens.color.textSubtle, whiteSpace: "nowrap" }}>
        Filtered by:
      </span>

      {/* Filter chips */}
      {activeFilters.map(([key, value]) => (
        <div
          key={key}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "#F0F0F0",
            border: `1px solid ${tokens.color.border}`,
            borderRadius: 999,
            padding: "3px 10px 3px 10px",
            fontSize: 12,
            color: tokens.color.textPrimary,
            fontWeight: 500,
          }}
        >
          <span style={{ color: tokens.color.textSubtle, fontWeight: 400 }}>{key}:</span>
          <span style={{ fontWeight: 600 }}>{value}</span>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(key)}
              title={`Remove ${key} filter`}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0 0 0 2px",
                fontSize: 13,
                color: tokens.color.textMuted,
                lineHeight: 1,
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = tokens.color.danger;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = tokens.color.textMuted;
              }}
            >
              ×
            </button>
          )}
        </div>
      ))}

      {/* Clear all */}
      <button
        type="button"
        onClick={onClear}
        style={{
          background: "none",
          border: `1px solid ${tokens.color.border}`,
          borderRadius: 999,
          padding: "3px 10px",
          fontSize: 12,
          color: tokens.color.textMuted,
          cursor: "pointer",
          fontFamily: tokens.font.body,
          whiteSpace: "nowrap",
          transition: "color 0.1s, border-color 0.1s",
        }}
        onMouseEnter={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = tokens.color.danger;
          btn.style.borderColor = tokens.color.danger;
        }}
        onMouseLeave={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = tokens.color.textMuted;
          btn.style.borderColor = tokens.color.border;
        }}
      >
        Clear all
      </button>
    </div>
  );
}
